import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  Grid,
  TextField,
  Tooltip,
  Typography,
} from '@weave-mui/material';
import { FormControlLabel } from '@mui/material';
import FormLabel, { formLabelVariants } from '@weave-mui/form-label';
import { InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material';

/**
 * BulkAddFieldSection - A reusable component for adding fields to multiple jobs at once
 *
 * This component provides a UI for bulk operations, allowing users to either select
 * a global field or create a custom field. For global fields with choices, it provides
 * a dropdown of values. For custom fields or string-type global fields, it provides
 * a text input.
 *
 * @param {Object} props - Component props
 * @param {Array} props.jobsData - Array of job objects containing test data
 * @param {string} props.currentEnvironment - Current environment key (e.g., "STG", "INT")
 * @param {Array} props.globalFields - Array of global field definitions with keys, types, and options
 * @param {Function} props.onAddField - Callback when field is added - receives (fieldKey, fieldValue, environment)
 * @param {Function} props.validateFieldKey - Validation function from parent - receives (fieldKey, environment), returns {isValid, errorMessage}
 * @param {Function} [props.validateFieldValue] - Validation function from parent - receives (fieldValue), returns {isValid, errorMessage}
 * @param {boolean} [props.disabled=false] - Whether the section should be disabled
 * @param {string} [props.className=''] - Additional CSS classes to apply to the container
 * @param {boolean} [props.showTitle=true] - Whether to render the section title
 *
 * @returns {JSX.Element|null} The bulk add field section component or null if less than 2 jobs
 *
 * @example
 * <BulkAddFieldSection
 *   jobsData={testDataState}
 *   currentEnvironment="STG"
 *   globalFields={globalFields}
 *   onAddField={(key, value, env) => handleBulkAdd(key, value, env)}
 *   validateFieldKey={(key, env) => validateKey(key, env)}
 * />
 */
const BulkAddFieldSection = ({
  jobsData = [],
  currentEnvironment = '',
  globalFields = [],
  onAddField = () => {},
  validateFieldKey = () => ({ isValid: true, errorMessage: '' }),
  validateFieldValue = () => ({ isValid: true, errorMessage: '' }),
  disabled = false,
  className = '',
  showTitle = true,
}) => {
  const HELPER_SLOT_HEIGHT_PX = 24; // 20px text line + ~4px top margin (keeps row stable across envs)
  const helperTextSx = {
    minHeight: `${HELPER_SLOT_HEIGHT_PX}px`,
    lineHeight: '20px',
    mt: '4px',
    mx: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  // Internal state for form inputs and feedback
  const [fieldKeyInput, setFieldKeyInput] = useState('');
  const [selectedFieldValue, setSelectedFieldValue] = useState('');
  const [customFieldValue, setCustomFieldValue] = useState('');
  const [currentGlobalField, setCurrentGlobalField] = useState(null);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' }); // type: 'success' | 'error' | 'info' | 'warning' | ''
  const [fieldKeyTouched, setFieldKeyTouched] = useState(false);
  const [fieldValueTouched, setFieldValueTouched] = useState(false);

  // Don't render if less than 2 jobs
  if (!jobsData || jobsData.length < 2) {
    return null;
  }

  const globalFieldKeyOptions = useMemo(
    () => (globalFields || []).map((field) => field.key).filter(Boolean),
    [globalFields],
  );

  /**
   * Normalize field value options to react-select format
   * Handles both string arrays and object arrays
   */
  const normalizeFieldValueOptions = useCallback((options) => {
    if (!options || !Array.isArray(options)) {
      return [];
    }

    return options.map((opt) => {
      if (typeof opt === 'string') return opt;
      if (typeof opt === 'object' && opt !== null) {
        if (typeof opt.value === 'string') return opt.value;
        if (typeof opt.label === 'string') return opt.label;
      }
      return String(opt);
    });
  }, []);

  /**
   * Get display value from a field value (handles both string and object formats)
   */
  const getDisplayValue = useCallback((value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value.value) return value.value;
    if (typeof value === 'object' && value.label) return value.label;
    return String(value);
  }, []);

  /**
   * Handle field key selection/creation
   * Updates the selected field and determines if it's a global field
   * Automatically populates default values for global fields
   * Resets replaceExisting checkbox each time
   */
  const applyFieldKey = useCallback(
    (nextKey) => {
      const trimmed = (nextKey || '').trim();
      setFieldKeyInput(trimmed);
      setFeedback({ type: '', message: '' });
      setReplaceExisting(false);

      if (!trimmed) {
        setCurrentGlobalField(null);
        setSelectedFieldValue('');
        setCustomFieldValue('');
        return;
      }

      const globalField =
        (globalFields || []).find((f) => f.key === trimmed) || null;
      setCurrentGlobalField(globalField);

      if (globalField) {
        if (globalField.type === 'choices') {
          const defaultValue = getDisplayValue(globalField.default);
          setSelectedFieldValue(defaultValue);
          setCustomFieldValue('');
        } else if (globalField.type === 'string') {
          const defaultValue = getDisplayValue(globalField.value);
          setCustomFieldValue(defaultValue);
          setSelectedFieldValue('');
        }
      } else {
        setSelectedFieldValue('');
        setCustomFieldValue('');
      }
    },
    [getDisplayValue, globalFields],
  );

  /**
   * Handle field value selection (for global fields with choices)
   */
  /**
   * Handle field value input change (for custom fields or string-type global fields)
   */
  const handleCustomFieldValueChange = (e) => {
    setCustomFieldValue(e.target.value);
    setFeedback({ type: '', message: '' });
  };

  /**
   * Handle add button click
   * Validates input, calls parent callback, and manages feedback
   */
  const handleAddClick = () => {
    const fieldKey = (fieldKeyInput || '').trim();

    if (!fieldKey) {
      setFieldKeyTouched(true);
      return;
    }

    // Determine the value to use based on field type
    let fieldValue;

    if (currentGlobalField) {
      // Global field
      if (currentGlobalField.type === 'choices') {
        // Choices type - use selected value or default
        fieldValue =
          selectedFieldValue || getDisplayValue(currentGlobalField.default);
      } else if (currentGlobalField.type === 'string') {
        // String type - use custom input or default value
        fieldValue =
          customFieldValue.trim() || getDisplayValue(currentGlobalField.value);
      }
    } else {
      // Custom field - use custom input
      fieldValue = customFieldValue.trim();
    }

    // Validate that we have a value
    if (!fieldValue) {
      setFieldValueTouched(true);
      return;
    }

    // Validate field key using parent's validation function
    const keyValidation = validateFieldKey(fieldKey, currentEnvironment);

    if (!keyValidation.isValid) {
      setFieldKeyTouched(true);
      return;
    }

    // Validate field value using parent's validation function
    const valueValidation = validateFieldValue(fieldValue);

    if (!valueValidation.isValid) {
      setFieldValueTouched(true);
      return;
    }

    // Call parent's add field handler
    try {
      onAddField(fieldKey, fieldValue, currentEnvironment, replaceExisting);

      // Set success feedback
      const fieldTypeInfo = currentGlobalField
        ? ` (global field, ${currentGlobalField.type})`
        : ' (custom field)';

      setFeedback({
        type: 'success',
        message: `Field '${fieldKey}' with value '${fieldValue}' added to ${jobsData.length} jobs successfully${fieldTypeInfo}`,
      });

      // Clear inputs and reset touched state
      setFieldKeyInput('');
      setSelectedFieldValue('');
      setCustomFieldValue('');
      setCurrentGlobalField(null);
      setReplaceExisting(false);
      setFieldKeyTouched(false);
      setFieldValueTouched(false);

      // Auto-clear success feedback after 5 seconds
      setTimeout(() => {
        setFeedback({ type: '', message: '' });
      }, 5000);
    } catch (error) {
      const errorMessage = error.message || 'Failed to add field to jobs';

      // Determine feedback type based on error message content
      // Partial success or all skipped scenarios should show as info/warning
      let feedbackType = 'error';
      let shouldClearInputs = false;

      if (
        errorMessage.includes('added to') &&
        errorMessage.includes('successfully')
      ) {
        // Partial success - some jobs were updated, some were skipped
        feedbackType = 'info';
        shouldClearInputs = true;
      } else if (errorMessage.includes('already exists in all')) {
        // All jobs already have the field - show as warning
        feedbackType = 'warning';
      }

      setFeedback({
        type: feedbackType,
        message: errorMessage,
      });

      // Clear inputs if partial success
      if (shouldClearInputs) {
        setFieldKeyInput('');
        setSelectedFieldValue('');
        setCustomFieldValue('');
        setCurrentGlobalField(null);
        setFieldKeyTouched(false);
        setFieldValueTouched(false);

        // Auto-clear feedback after 7 seconds for info messages
        setTimeout(() => {
          setFeedback({ type: '', message: '' });
        }, 7000);
      }
    }
  };

  // Determine if button should be disabled
  const getFieldValueForValidation = () => {
    if (currentGlobalField) {
      if (currentGlobalField.type === 'choices') {
        return (
          selectedFieldValue || getDisplayValue(currentGlobalField.default)
        );
      } else if (currentGlobalField.type === 'string') {
        return (
          customFieldValue.trim() || getDisplayValue(currentGlobalField.value)
        );
      }
    }
    return customFieldValue.trim();
  };

  const isButtonDisabled =
    disabled || !fieldKeyInput.trim() || !getFieldValueForValidation();

  const fieldValueForDisplay = getFieldValueForValidation();
  const fieldKeyValidation = validateFieldKey(
    fieldKeyInput.trim(),
    currentEnvironment,
  );
  const fieldValueValidation = validateFieldValue(fieldValueForDisplay || '');

  const fieldKeyError = fieldKeyTouched
    ? fieldKeyValidation.isValid
      ? ''
      : fieldKeyValidation.errorMessage
    : '';
  const fieldValueError =
    fieldValueTouched && fieldValueForDisplay
      ? fieldValueValidation.isValid
        ? ''
        : fieldValueValidation.errorMessage
      : '';

  const titleSpacing = showTitle ? 1.5 : 0;
  const checkboxTopSpacing = showTitle ? 2 : 0;

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        px: 2, // 16px
        py: 0,
        borderRadius: 2,
        mb: 0,
        boxSizing: 'border-box',
      }}
    >
      {showTitle && (
        <Typography
          variant="body1"
          color="textPrimary"
          sx={{ fontSize: '16px', fontWeight: 600, mb: titleSpacing }}
        >
          Add field to all {jobsData.length} selected jobs
        </Typography>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 0,
          mt: checkboxTopSpacing,
        }}
      >
        <FormControlLabel
          disabled={disabled}
          control={
            <Checkbox
              checked={replaceExisting}
              onChange={(e) => setReplaceExisting(e.target.checked)}
              size="small"
              sx={{ p: 0, m: 0 }}
            />
          }
          label={
            <Typography variant="body2" color="textPrimary">
              Replace existing field values (if field already exists in some
              jobs)
            </Typography>
          }
          sx={{
            m: 0,
            // tighten checkbox <-> label spacing
            gap: 0.5,
            '& .MuiFormControlLabel-label': {
              marginLeft: 0,
            },
          }}
        />
        <Tooltip title="When checked, the field value will be updated in jobs that already have this field. When unchecked, jobs with existing field will be skipped.">
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              color: 'text.secondary',
              cursor: 'default',
              lineHeight: 0,
            }}
          >
            <InfoOutlinedIcon fontSize="small" />
          </Box>
        </Tooltip>
      </Box>

      <Grid container spacing={2} alignItems="flex-end">
        <Grid item xs={12} md={5}>
          <FormControl fullWidth disabled={disabled} error={!!fieldKeyError}>
            <FormLabel htmlFor="bulk-field-key" variant={formLabelVariants.TOP}>
              Field key
            </FormLabel>
            <Autocomplete
              id="bulk-field-key"
              freeSolo
              disableClearable={false}
              options={globalFieldKeyOptions}
              value={fieldKeyInput}
              inputValue={fieldKeyInput}
              onInputChange={(_, newInputValue) => {
                applyFieldKey(newInputValue);
              }}
              onChange={(_, newValue) => {
                applyFieldKey(
                  typeof newValue === 'string' ? newValue : newValue || '',
                );
              }}
              onBlur={() => setFieldKeyTouched(true)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  variant="outlined"
                  placeholder="Select global field or type custom field…"
                  error={!!fieldKeyError}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 38,
                    },
                  }}
                />
              )}
            />
            <FormHelperText sx={helperTextSx}>
              {fieldKeyError ||
                (fieldKeyInput && !currentGlobalField
                  ? 'Custom field (letters, numbers, underscores, hyphens)'
                  : currentGlobalField
                    ? `Global field (${currentGlobalField.type})`
                    : ' ')}
            </FormHelperText>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={5}>
          {currentGlobalField && currentGlobalField.type === 'choices' ? (
            <FormControl
              fullWidth
              disabled={disabled}
              error={!!fieldValueError}
            >
              <FormLabel
                htmlFor="bulk-field-value"
                variant={formLabelVariants.TOP}
              >
                Field value
              </FormLabel>
              <Autocomplete
                id="bulk-field-value"
                freeSolo
                disableClearable={false}
                options={normalizeFieldValueOptions(currentGlobalField.options)}
                value={selectedFieldValue}
                inputValue={selectedFieldValue}
                onInputChange={(_, newInputValue) => {
                  setSelectedFieldValue(newInputValue || '');
                  setFeedback({ type: '', message: '' });
                }}
                onChange={(_, newValue) => {
                  setSelectedFieldValue(
                    typeof newValue === 'string' ? newValue : newValue || '',
                  );
                  setFeedback({ type: '', message: '' });
                }}
                onBlur={() => setFieldValueTouched(true)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    variant="outlined"
                    placeholder="Select value or type…"
                    error={!!fieldValueError}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: 38,
                      },
                    }}
                  />
                )}
              />
              <FormHelperText sx={helperTextSx}>
                {fieldValueError ||
                  (!selectedFieldValue
                    ? `Will use default: ${getDisplayValue(currentGlobalField.default)}`
                    : ' ')}
              </FormHelperText>
            </FormControl>
          ) : (
            <FormControl
              fullWidth
              disabled={disabled}
              error={!!fieldValueError}
            >
              <FormLabel
                htmlFor="bulk-field-value"
                variant={formLabelVariants.TOP}
              >
                Field value
              </FormLabel>
              <TextField
                id="bulk-field-value"
                size="small"
                variant="outlined"
                fullWidth
                value={customFieldValue}
                onChange={handleCustomFieldValueChange}
                onBlur={() => setFieldValueTouched(true)}
                placeholder="Enter value…"
                disabled={disabled}
                error={!!fieldValueError}
                sx={{
                  '& .MuiInputBase-root': {
                    height: 38,
                  },
                }}
              />
              <FormHelperText sx={helperTextSx}>
                {fieldValueError ||
                  (currentGlobalField &&
                  currentGlobalField.type === 'string' &&
                  !customFieldValue
                    ? `Will use default: ${getDisplayValue(currentGlobalField.value)}`
                    : ' ')}
              </FormHelperText>
            </FormControl>
          )}
        </Grid>

        <Grid
          item
          xs={12}
          md={2}
          sx={{ display: 'flex', alignItems: 'flex-end' }}
        >
          <Box sx={{ width: '100%' }}>
            <Button
              variant="contained"
              onClick={handleAddClick}
              disabled={isButtonDisabled}
              fullWidth
              color="primary"
              sx={{ height: 38 }}
            >
              Add to all jobs
            </Button>
            {/* reserve helperText slot so button never shifts when helper text appears/disappears */}
            <Box sx={{ height: `${HELPER_SLOT_HEIGHT_PX}px`, mt: '8px' }} />
          </Box>
        </Grid>
      </Grid>

      {feedback.message && (
        <Box sx={{ mt: 2 }}>
          <Alert
            severity={
              feedback.type === 'success'
                ? 'success'
                : feedback.type === 'info'
                  ? 'info'
                  : feedback.type === 'warning'
                    ? 'warning'
                    : 'error'
            }
          >
            <Typography variant="body2" color="textPrimary">
              {feedback.message}
            </Typography>
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default BulkAddFieldSection;
